import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { api, priceView } from "@/lib/api";

const CartContext = createContext(null);
const STORAGE_KEY = "dw_cart_v1";
const WISHLIST_KEY = "dw_wishlist_local_v1";

const readCart = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); } catch { return []; }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(readCart);
  const [stockIssues, setStockIssues] = useState({});
  const [localWishlist, setLocalWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY) || "[]"); } catch { return []; }
  });

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem(WISHLIST_KEY, JSON.stringify(localWishlist)); }, [localWishlist]);

  // Server-side stock check against the central inventory (never trust cached product.quantity).
  const addItem = async (product, quantity = 1) => {
    if (!product) return { ok: false, message: "Unavailable" };
    const existing = items.find((i) => i.id === product.id);
    const requested = (existing ? existing.quantity : 0) + quantity;
    let check;
    try {
      const r = await api.post("/cart/validate", [{ product_id: product.id, quantity: requested }]);
      check = r.data.items[0];
    } catch {
      return { ok: false, message: "Could not verify stock. Please try again." };
    }
    if (check.available <= 0) return { ok: false, message: check.message === "Currently unavailable" ? "Currently unavailable." : "Sold out" };
    if (!check.ok) {
      return { ok: false, message: check.inventory_type === "UNIQUE_ITEM" ? "This is a one-of-a-kind item — only 1 available." : `Only ${check.max_quantity} available.` };
    }
    const maxAvail = check.max_quantity;
    setItems((prev) => {
      const ex = prev.find((i) => i.id === product.id);
      if (ex) return prev.map((i) => (i.id === product.id ? { ...i, quantity: Math.min(requested, maxAvail), max_available: maxAvail } : i));
      const pv = priceView(product);
      return [...prev, {
        id: product.id, slug: product.slug, product_name: product.product_name,
        price: pv.now, was: pv.was, sale_price: product.sale_price ?? null,
        compare_at_price: product.compare_at_price,
        featured_image: product.featured_image, quantity: Math.min(quantity, maxAvail),
        max_available: maxAvail, condition: product.condition, brand: product.brand,
        inventory_type: check.inventory_type,
      }];
    });
    return { ok: true };
  };

  // Re-check every line against live inventory (cart open, checkout start, order submit).
  const validateCart = useCallback(async () => {
    if (items.length === 0) { setStockIssues({}); return { ok: true, issues: {} }; }
    try {
      const r = await api.post("/cart/validate", items.map((i) => ({ product_id: i.id, quantity: i.quantity })));
      const issues = {};
      r.data.items.forEach((c) => {
        if (!c.ok) issues[c.product_id] = c.available <= 0 ? (c.message === "Currently unavailable" ? "Currently unavailable" : "Sold out") : `Only ${c.max_quantity} available.`;
      });
      setItems((prev) => prev.map((i) => {
        const c = r.data.items.find((x) => x.product_id === i.id);
        if (!c) return i;
        const pv = c.price != null ? priceView({ price: c.price, sale_price: c.sale_price, compare_at_price: c.compare_at_price }) : null;
        return {
          ...i,
          max_available: c.max_quantity,
          quantity: c.max_quantity > 0 ? Math.min(i.quantity, c.max_quantity) : i.quantity,
          ...(pv ? { price: pv.now, was: pv.was, sale_price: c.sale_price ?? null, compare_at_price: c.compare_at_price } : {}),
        };
      }));
      setStockIssues(issues);
      return { ok: Object.keys(issues).length === 0, issues };
    } catch {
      return { ok: true, issues: {} };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((i) => `${i.id}:${i.quantity}`).join(",")]);

  const updateQuantity = (id, quantity) => {
    setItems((prev) => prev
      .map((i) => (i.id === id ? { ...i, quantity: Math.max(1, Math.min(quantity, i.max_available || quantity)) } : i))
      .filter((i) => i.quantity > 0));
  };

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const clearCart = () => setItems([]);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const totalCount = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const toggleLocalWishlist = (productId) => {
    setLocalWishlist((prev) => prev.includes(productId) ? prev.filter((p) => p !== productId) : [...prev, productId]);
  };
  const isInLocalWishlist = (id) => localWishlist.includes(id);

  const value = useMemo(() => ({
    items, addItem, updateQuantity, removeItem, clearCart, subtotal, totalCount, validateCart, stockIssues,
    localWishlist, toggleLocalWishlist, isInLocalWishlist,
  }), [items, subtotal, totalCount, validateCart, stockIssues, localWishlist]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
